import { memo, useState } from "react";
import { Loader2, Smartphone, Sparkles } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

function UpiIntentButtons({ upiIntent, snap, amount, txnId, preferredApp, bucketRecommendedApp, onIntentTap }) {
    // Tiny ephemeral toast — surfaces "Opening PhonePe…" right after the
    // deep-link tap so the user knows the intent fired even if the OS
    // chooser sheet takes a moment to appear.
    const [toast, setToast] = useState(null);
    if (!upiIntent) return null;
    // Detect mobile vs desktop. UPI deep-link intents only resolve on mobile.
    const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isMobile = isAndroid || isIOS || /Mobile/i.test(ua);

    if (!isMobile) {
        return (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-center">
                <div className="text-xs font-medium text-blue-900">Scan the QR with your phone&apos;s UPI app</div>
                <div className="text-[11px] text-blue-700 mt-1">UPI app shortcuts only work on mobile devices.</div>
            </div>
        );
    }

    // Build per-app deep links.
    // - On Android, branded apps need Intent URLs (with `package=`) to reliably
    //   open a specific UPI app. The legacy `tez://`, `phonepe://`, `paytmmp://`
    //   schemes are unreliable on modern Android.
    // - On iOS, the OPPOSITE is true: intent:// URLs don't work, and the
    //   generic `upi://pay?...` scheme opens the OS picker which often does
    //   nothing (iOS < 17). Each Indian UPI app registers its OWN URL scheme
    //   that we must hit directly: `tez://` for GPay, `phonepe://`, `paytmmp://`.
    const upiQuery = upiIntent.replace(/^upi:\/\/pay\??/, "");
    const iosSchemeUrl = {
        gpay: `tez://upi/pay?${upiQuery}`,        // Google Pay India uses the legacy Tez scheme on iOS
        phonepe: `phonepe://pay?${upiQuery}`,
        paytm: `paytmmp://pay?${upiQuery}`,
    };
    const intentUrl = (pkg, appId) => {
        if (isAndroid) return `intent://pay?${upiQuery}#Intent;scheme=upi;package=${pkg};end`;
        if (isIOS && appId && iosSchemeUrl[appId]) return iosSchemeUrl[appId];
        // Desktop / unknown: fall back to generic UPI so QR-scanners still work.
        return upiIntent;
    };
    const baseApps = [
        { id: "gpay", label: "Google Pay", color: "bg-white border-2 border-slate-200 hover:border-blue-400 text-slate-900", href: intentUrl("com.google.android.apps.nbu.paisa.user", "gpay") },
        { id: "phonepe", label: "PhonePe", color: "bg-white border-2 border-slate-200 hover:border-purple-400 text-slate-900", href: intentUrl("com.phonepe.app", "phonepe") },
        { id: "paytm", label: "Paytm", color: "bg-white border-2 border-slate-200 hover:border-cyan-400 text-slate-900", href: intentUrl("net.one97.paytm", "paytm") },
    ];

    // Effective recommendation: customer's last-tapped app wins. If the customer
    // has no history, fall back to the partner-level "top app for this amount
    // bucket" — first-time visitors still see the optimal default.
    const effectiveRec = preferredApp || bucketRecommendedApp;
    const recSource = preferredApp ? "personal" : (bucketRecommendedApp ? "bucket" : null);

    // Reorder so the recommended app appears first.
    const apps = effectiveRec
        ? [
            ...baseApps.filter((a) => a.id === effectiveRec),
            ...baseApps.filter((a) => a.id !== effectiveRec),
        ]
        : baseApps;

    // Tiny ephemeral toast — surfaces "Opening PhonePe…" right after the
    // deep-link tap so the user knows the intent fired even if the OS
    // chooser sheet takes a moment to appear.
    // On iOS we also detect "app didn't open" — if the page is still visible
    // 2.5s after the tap, the URL scheme silently failed (app not installed).
    const onTap = (appId) => {
        const labelMap = { upi: "Opening UPI app…", gpay: "Opening Google Pay…", phonepe: "Opening PhonePe…", paytm: "Opening Paytm…" };
        const installHint = { gpay: "Google Pay", phonepe: "PhonePe", paytm: "Paytm" };
        setToast(labelMap[appId] || "Opening UPI app…");
        const tapTime = Date.now();
        // Notify parent so it can start its decline-detection window.
        if (typeof onIntentTap === "function") onIntentTap();
        setTimeout(() => {
            // If we're still visible, the app didn't pick up the scheme —
            // most likely it's not installed (or scheme is registered but app
            // declined to handle). Show a helpful nudge instead of silence.
            if (isIOS && document.visibilityState === "visible" && Date.now() - tapTime < 4000 && installHint[appId]) {
                setToast(`${installHint[appId]} didn't open — is the app installed?`);
                setTimeout(() => setToast(null), 3500);
            } else {
                setToast(null);
            }
        }, 2500);
        // Fire-and-forget — don't block the deep-link click. If it fails (e.g.
        // session already ended), it's irrelevant for the customer's flow.
        fetch(`${BACKEND_URL}/api/public/payment-page/${txnId}/paid-via`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ app: appId }),
            keepalive: true,
        }).catch(() => { /* non-fatal */ });
    };

    return (
        <div className="space-y-1.5 sm:space-y-2.5 relative">
            {toast && (
                <div data-testid="upi-intent-toast"
                     className="fixed left-1/2 top-4 -translate-x-1/2 z-50 bg-slate-900 text-white px-4 py-2 rounded-full text-xs font-medium shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {toast}
                </div>
            )}
            <a
                data-testid="upi-intent-pay-now"
                href={upiIntent}
                onClick={() => onTap("upi")}
                className="group flex items-center justify-center gap-2 w-full py-2.5 sm:py-3.5 px-4 rounded-lg bg-gradient-to-br from-blue-600 to-emerald-600 text-white font-semibold text-sm sm:text-base shadow-md hover:shadow-lg hover:from-blue-700 hover:to-emerald-700 active:scale-[0.99] transition-all"
            >
                <Smartphone className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                <span>Pay ₹{amount?.toLocaleString?.("en-IN") ?? amount} with UPI app</span>
            </a>
            <div className="hidden sm:block text-[10px] text-center text-slate-400 -mt-1">or pick a specific app</div>
            <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                {apps.map((a) => {
                    const isRecommended = effectiveRec && a.id === effectiveRec;
                    const badgeText = recSource === "personal" ? "Last used" : "Most popular";
                    return (
                        <a key={a.id}
                            data-testid={`upi-intent-${a.id}`}
                            href={a.href}
                            onClick={() => onTap(a.id)}
                            className={`relative py-1.5 sm:py-2.5 px-2 rounded-md text-[11px] sm:text-sm font-medium text-center transition-all ${a.color} ${isRecommended ? "ring-2 ring-emerald-400 ring-offset-1" : ""}`}>
                            {a.label}
                            {isRecommended && (
                                <span data-testid={`upi-recommended-${a.id}`} className="absolute -top-2 -right-2 inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-wider text-white bg-emerald-500 px-1.5 py-0.5 rounded-full shadow-sm">
                                    <Sparkles className="w-2.5 h-2.5" /> {badgeText}
                                </span>
                            )}
                        </a>
                    );
                })}
            </div>
        </div>
    );
}

// memo: stable props (upiIntent / snap / amount / txnId / preferredApp /
// bucketRecommendedApp / onIntentTap) — none of these change between
// status-poll ticks, so we skip the full intent-button diff every 4-15s.
// `onIntentTap` MUST be stable across renders (wrap with useCallback in
// the parent) for memo to be effective.
export default memo(UpiIntentButtons);
